import { cn } from '@coas/ui/lib';
import Image from 'next/image';
import Link from 'next/link';
import { memo, useEffect, useMemo, useState } from 'react';
import type { RouterOutputs } from 'trpc/shared';
import { useRemoveAllFromCart, useToggleCartPostponed } from '~/entities/Cart';
import { PriceWithDiscount } from '~/entities/DisplayedPrice';
import IconTrash from '~/shared/assets/icons/trash.svg';
import { imgLoader } from '~/shared/lib';
import { Counter } from './Counter';

type CartCardProps = {
    cartItem: RouterOutputs['cart']['getAll']['items'][number];
    interactive?: boolean;
};

export default memo(({ cartItem, interactive = false }: CartCardProps) => {
    const productImage = useMemo(() => cartItem.product.image ?? null, [cartItem.product.image]);

    const togglePostponed = useToggleCartPostponed();
    const isToggling = togglePostponed.isPending;

    const removeAll = useRemoveAllFromCart();
    const isRemoving = removeAll.isPending;

    const inOrder = !cartItem.postponed;

    const [toggleVisualFrom, setToggleVisualFrom] = useState<null | boolean>(null);
    const visualInOrder = toggleVisualFrom ?? inOrder;

    useEffect(() => {
        if (!isToggling) setToggleVisualFrom(null);
    }, [isToggling]);

    const handleTogglePostponed = () => {
        if (!interactive || isToggling) return;
        setToggleVisualFrom(inOrder);
        togglePostponed.mutate({ productPriceId: cartItem.productPriceId, postponed: !cartItem.postponed });
    };

    const handleRemoveAll = () => {
        if (!interactive || isRemoving) return;
        removeAll.mutate({ productPriceId: cartItem.productPriceId });
    };

    const canHoverToggle = interactive && !isToggling;
    const canHoverRemove = interactive && !isRemoving;

    return (
        <div className={cn('grid grid-cols-[7rem_1fr] gap-x-4')}>
            <div className='relative h-36 w-28 bg-white'>
                {!!productImage?.key && (
                    <Link href={`/products/${cartItem.product.slug}`} className='relative block h-full w-full'>
                        <Image
                            src={productImage.key}
                            loader={imgLoader({
                                src: productImage.key,
                                width: productImage.width,
                                height: productImage.height,
                            })}
                            placeholder='blur'
                            blurDataURL={productImage.lqip ?? undefined}
                            className={cn(
                                productImage.width && productImage.height && productImage.width > productImage.height
                                    ? 'object-contain'
                                    : 'object-cover',
                                cartItem.postponed && 'opacity-70',
                            )}
                            alt={productImage.alt ?? ''}
                            fill
                        />
                    </Link>
                )}
            </div>

            <div className={cn('flex min-w-0 flex-col gap-y-1', cartItem.postponed && 'text-neutral-500')}>
                <div className='flex items-start justify-between gap-2'>
                    <span
                        className={cn(
                            'truncate text-14 font-500 uppercase text-neutral-500',
                            cartItem.postponed && 'text-neutral-400',
                        )}
                    >
                        {cartItem.product.categories.map(c => c.category.name).join(', ')}
                    </span>

                    {interactive && (
                        <div className='hidden lg:flex items-center gap-2'>
                            <button
                                type='button'
                                onClick={handleTogglePostponed}
                                disabled={isToggling}
                                aria-pressed={!visualInOrder}
                                title={visualInOrder ? 'Отложить' : 'Вернуть'}
                                className={cn(
                                    'box-border h-8 px-3 text-12 uppercase rounded-none transition-colors',
                                    'min-w-[7.5rem] whitespace-nowrap',
                                    'ring-1 ring-inset ring-neutral-900',
                                    'disabled:cursor-not-allowed',
                                    isToggling && toggleVisualFrom !== null && 'animate-pulse',
                                    visualInOrder
                                        ? 'bg-transparent text-neutral-900'
                                        : 'bg-neutral-900 text-neutral-50',
                                    canHoverToggle &&
                                        (visualInOrder
                                            ? 'hover:bg-neutral-900 hover:text-neutral-50'
                                            : 'hover:bg-neutral-800'),
                                )}
                            >
                                {visualInOrder ? 'Отложить' : 'Вернуть'}
                            </button>

                            <button
                                type='button'
                                onClick={handleRemoveAll}
                                disabled={isRemoving}
                                aria-label='Удалить из корзины'
                                title='Удалить из корзины'
                                className={cn(
                                    'box-border inline-flex size-8 items-center justify-center rounded-none text-neutral-900 transition-colors',
                                    'ring-1 ring-inset ring-neutral-900',
                                    'disabled:cursor-not-allowed',
                                    isRemoving && 'animate-pulse',
                                    canHoverRemove && 'hover:bg-neutral-900 hover:text-neutral-50',
                                )}
                            >
                                <IconTrash className='size-4' aria-hidden='true' />
                            </button>
                        </div>
                    )}
                </div>

                <Link href={`/products/${cartItem.product.slug}`} className='line-clamp-2 font-500 leading-[1.1]'>
                    {cartItem.product.name}
                </Link>

                <PriceWithDiscount displayPrice={cartItem.displayedPrice} />

                <span className='text-14'>Размер: {cartItem.size.value}</span>

                <div className='flex-1' />

                <div className='relative'>
                    <div className={cn(cartItem.postponed && 'opacity-40')}>
                        <Counter cartItem={cartItem} interactive={interactive} />
                    </div>
                    {interactive && cartItem.postponed && (
                        <div
                            className='absolute inset-0 cursor-not-allowed'
                            aria-hidden
                            title='Товар отложен — измените статус, чтобы менять количество'
                        />
                    )}
                </div>
            </div>

            {interactive && (
                <div className='col-span-2 mt-2 grid grid-cols-2 gap-2 lg:hidden px-px'>
                    <button
                        type='button'
                        onClick={handleTogglePostponed}
                        disabled={isToggling}
                        aria-pressed={!visualInOrder}
                        title={visualInOrder ? 'Сохранить на потом' : 'Вернуть в заказ'}
                        className={cn(
                            'box-border h-9 w-full text-12 uppercase rounded-none transition-colors',
                            'ring-1 ring-inset ring-neutral-900',
                            'disabled:cursor-not-allowed',
                            isToggling && toggleVisualFrom !== null && 'animate-pulse',
                            visualInOrder ? 'bg-transparent text-neutral-900' : 'bg-neutral-900 text-neutral-50',
                        )}
                    >
                        {visualInOrder ? 'Отложить' : 'Вернуть'}
                    </button>

                    <button
                        type='button'
                        onClick={handleRemoveAll}
                        disabled={isRemoving}
                        title='Удалить из корзины'
                        className={cn(
                            'box-border h-9 w-full text-12 uppercase rounded-none transition-colors text-neutral-900',
                            'ring-1 ring-inset ring-neutral-900',
                            'disabled:cursor-not-allowed',
                            isRemoving && 'animate-pulse',
                        )}
                    >
                        Удалить
                    </button>
                </div>
            )}
        </div>
    );
});
